(() => {
  if (window.__collectionCarouselPanelNavigationLoaded) return;
  window.__collectionCarouselPanelNavigationLoaded = true;

  const ROOT_SELECTOR = '[data-ccp-root]';
  const CARD_SELECTOR = '.collection-carousel-panel__card';
  const EDGE_TOLERANCE = 3;

  class CollectionCarouselPanelNavigation {
    constructor(root) {
      this.root = root;
      this.scrollArea = root.querySelector('[data-ccp-scroll-area]');
      this.previousButton = root.querySelector('[data-ccp-nav="prev"]');
      this.nextButton = root.querySelector('[data-ccp-nav="next"]');
      this.frame = null;

      if (!this.scrollArea || !this.previousButton || !this.nextButton) return;

      this.onPreviousClick = this.onPreviousClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      this.previousButton.addEventListener('click', this.onPreviousClick);
      this.nextButton.addEventListener('click', this.onNextClick);
      this.scrollArea.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scrollArea);

      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(this.scrollArea, {
        childList: true,
        subtree: true
      });

      requestAnimationFrame(this.update);
      window.setTimeout(this.update, 250);
    }

    getCards() {
      return Array.from(this.scrollArea.querySelectorAll(CARD_SELECTOR));
    }

    getScrollStep() {
      const cards = this.getCards();

      if (cards.length > 1) {
        const firstOffset = cards[0].offsetLeft;
        const secondOffset = cards[1].offsetLeft;
        const measuredStep = Math.abs(secondOffset - firstOffset);

        if (measuredStep > 1) return measuredStep;
      }

      const firstCard = cards[0];

      if (!firstCard) {
        return Math.max(this.scrollArea.clientWidth * 0.8, 1);
      }

      const list = firstCard.parentElement;
      const listStyles = list ? window.getComputedStyle(list) : null;
      const gap = listStyles
        ? parseFloat(listStyles.columnGap || listStyles.gap || '0')
        : 0;

      return firstCard.getBoundingClientRect().width + gap;
    }

    scroll(direction) {
      const step = this.getScrollStep();

      this.scrollArea.scrollBy({
        left: direction * step,
        behavior: 'smooth'
      });

      window.setTimeout(this.update, 450);
    }

    onPreviousClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.previousButton.disabled) {
        this.scroll(-1);
      }
    }

    onNextClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.nextButton.disabled) {
        this.scroll(1);
      }
    }

    onScroll() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.update);
    }

    update() {
      const maximumScroll = Math.max(
        this.scrollArea.scrollWidth - this.scrollArea.clientWidth,
        0
      );
      const currentScroll = Math.max(this.scrollArea.scrollLeft, 0);
      const hasOverflow = maximumScroll > EDGE_TOLERANCE;

      this.previousButton.classList.toggle('is-available', hasOverflow);
      this.nextButton.classList.toggle('is-available', hasOverflow);

      this.previousButton.disabled =
        !hasOverflow || currentScroll <= EDGE_TOLERANCE;

      this.nextButton.disabled =
        !hasOverflow ||
        currentScroll >= maximumScroll - EDGE_TOLERANCE;
    }

    destroy() {
      this.previousButton?.removeEventListener('click', this.onPreviousClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.scrollArea?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();

      if (this.frame) cancelAnimationFrame(this.frame);

      delete this.root.dataset.ccpNavigationInitialized;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      if (root.dataset.ccpNavigationInitialized === 'true') return;

      root.dataset.ccpNavigationInitialized = 'true';
      root.__ccpNavigation = new CollectionCarouselPanelNavigation(root);
    });
  };

  const destroy = (scope) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => root.__ccpNavigation?.destroy());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), {
      once: true
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    root?.__ccpNavigation?.update();
  });

  const initTouchArrows = (scope = document) => {
    scope.querySelectorAll(CARD_SELECTOR).forEach(card => {
      card.addEventListener('touchstart', () => {
        document.querySelectorAll(CARD_SELECTOR).forEach(c => c.classList.remove('is-touched'));
        card.classList.add('is-touched');
      }, { passive: true });
    });
  };

  document.addEventListener('touchstart', e => {
    if (!e.target.closest(CARD_SELECTOR)) {
      document.querySelectorAll(CARD_SELECTOR).forEach(c => c.classList.remove('is-touched'));
    }
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTouchArrows(document), { once: true });
  } else {
    initTouchArrows(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initTouchArrows(event.target);
  });
})();